import { Address } from "viem";
import { CHARITY_GOAL_FRACTION } from "../utils/Constants";
import {
  xDrawOpenedEvent,
  xDrawUpdatedEvent,
  xLotteryInfo,
} from "../web3/Types";
import { LotteryConfig } from "./LotteryConfig";
import { USDC } from "./UsdcValue";

export class DrawInfo {
  drawNumber: bigint;
  drawConfig: LotteryConfig;
  jackpot: USDC;
  raised: USDC;
  prizePool: USDC;
  charityGoal: USDC;
  charityAddress?: Address;

  constructor(
    drawNumber: bigint,
    drawConfig: LotteryConfig,
    jackpot: USDC,
    raised: USDC,
    prizePool: USDC,
    charityGoal: USDC,
    charityAddress?: Address,
  ) {
    this.drawNumber = drawNumber;
    this.drawConfig = drawConfig;
    this.jackpot = jackpot;
    this.raised = raised;
    this.prizePool = prizePool;
    this.charityGoal = charityGoal;
    this.charityAddress = charityAddress;
  }

  static fromDrawOpenedEvent(
    event: xDrawOpenedEvent,
  ): DrawInfo {
    const drawConfig = LotteryConfig.fromLotteryConfig(event.config);
    return new DrawInfo(
      event.drawNumber,
      drawConfig,
      new USDC(event.jackpot),
      new USDC(0n),
      new USDC(0n),
      new USDC(
        BigInt(
          Math.floor(
            Number(event.jackpot) *
              Number(drawConfig.revenueDistribution.charityCut) *
              CHARITY_GOAL_FRACTION,
          ),
        ),
      ),
      event.charityAddress,
    );
  }

  static fromLotteryInfo(info: xLotteryInfo): DrawInfo {
    const drawConfig = LotteryConfig.fromLotteryConfig(info.lotteryConfig);
    return new DrawInfo(
      info.drawNumber,
      drawConfig,
      new USDC(info.jackpot),
      new USDC(info.proceeds.donations),
      new USDC(info.proceeds.prizes),
      new USDC(
        BigInt(
          Math.floor(
            Number(info.jackpot) *
              Number(drawConfig.revenueDistribution.charityCut) *
              CHARITY_GOAL_FRACTION,
          ),
        ),
      ),
      info.charityAddress,
    );
  }

  with(
    drawConfig?: LotteryConfig,
    jackpot?: USDC,
    raised?: USDC,
    prizePool?: USDC,
    charityGoal?: USDC,
    charityAddress?: Address,
    drawNumber?: bigint,
  ): DrawInfo {
    const newDrawConfig: LotteryConfig = drawConfig ?? this.drawConfig;
    const newJackpot: USDC = jackpot ?? this.jackpot;
    const newRaised: USDC = raised ?? this.raised;
    const newPrizePool: USDC = prizePool ?? this.prizePool;
    const newCharityGoal: USDC = charityGoal ?? this.charityGoal;
    const newCharityInstitution: Address | undefined =
      charityAddress ?? this.charityAddress;
    const newDrawNumber: bigint | undefined = drawNumber ?? this.drawNumber;
    return new DrawInfo(
      newDrawNumber,
      newDrawConfig,
      newJackpot,
      newRaised,
      newPrizePool,
      newCharityGoal,
      newCharityInstitution,
    );
  }

  updateWithDrawUpdatedEvent(event: xDrawUpdatedEvent): DrawInfo {
    return this.with(
      undefined,
      undefined,
      undefined,
      new USDC(event.proceeds.donations),
      new USDC(event.proceeds.prizes),
      undefined,
      undefined,
    );
  }

  toString(): string {
    return `DrawInfo(drawNumber=${this.drawNumber}, jackpot=${this.jackpot.toString()}, raised=${this.raised.toString()}, prizePool=${this.prizePool.toString()}, charityGoal=${this.charityGoal.toString()}, charityAddress=${this.charityAddress}, drawConfig=${this.drawConfig.toString()})`;
  }
}
